import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Form, Col, Tooltip, Result, Empty } from '@bedrock/components'
import Error from "@bedrock/components/es/Empty/images/small/Error";
import { ColProps } from '@bedrock/components/lib/Col'
import { HelpCircle } from '@bedrock/icons-react'
import { ErrorBoundary } from "react-error-boundary";
import { SFormElement, ComponentProps, SFormComponentClass } from "../SFormElement";
import cx from 'classnames'
import './index.less'
import {formItemKeyToList, DefaultRequiredRuleMap} from "#components/SForm/SFormItem/utils";
import FormItemValueTransform from "#components/SForm/SFormItem/FormItemValueTransform";
import _ from 'lodash';
import { TooltipProps } from '@bedrock/components/es/Tooltip';


export const getLabelTip = (label, labelTip) => {
  let toolTipProps = {};
  if (_.isObject(labelTip) && !React.isValidElement(labelTip)) {
    toolTipProps = labelTip;
  }
  return (
      <>
        <span style={{ marginRight: '4px' }}>{label}</span>
        <span className='label-icon'>
          <Tooltip title={labelTip} placement='top' {...toolTipProps}>
            <HelpCircle />
          </Tooltip>
        </span>
      </>
  )
}

// 函数生成T或者T本身
type AbleFunc<T, params = any> = T | ((params: params) => T)

type AbleFuncObj<T> = {
  [P in keyof T]: AbleFunc<T[P], any>
}

export type FormItemKeyType = (string | number)[] | string | number;

export type SFormItemPropsData<T extends SFormComponentClass = SFormComponentClass> = {
  formItemKeyMap?: Record<string, FormItemKeyType>;
  formItem?: boolean;
  formItemKey: FormItemKeyType;
  renderData?: any;
  label?: string | number;
  labelTip?: string | Partial<TooltipProps> | React.ReactElement;
  labelText?: string;
  required?: boolean;
  componentClass?: T;
  className?: string;
  view?: boolean;
  show?: boolean;
  processType?: string;
  col?: number | ColProps; // 布局
  offsetAfter?: number; // 偏移
  elementProps?: ComponentProps<T>;
  hideError?: boolean; // 是否隐藏错误提示
  render?: (renderData: any, itemProps: SFormItemPropsData) => React.ReactElement;
  labelWrapper?: (props: { label: any }, renderData: any) => React.ReactElement;
  watchChildFormValidate?: boolean | 'string'; // 是否监听子元素Form的校验，子元素的ref如果有validateFields方法，会被添加进校验队列
} & Omit<React.ComponentProps<typeof Form.Item>, 'label' | 'required' | 'name' | 'className'>;

export type SFormItemProps<T extends FormItemKeyType = FormItemKeyType, U extends SFormComponentClass = SFormComponentClass> = {
  formItemKey: T;
} & AbleFuncObj<Omit<SFormItemPropsData<U>, 'formItemKey'>>

// 函数参数，不再转化
const FUNCTION_CONFIG = ['render', 'labelWrapper']

// AbleFuncObj 转化为 SFormItemPropsData
export const configFunctionToValue = (props: SFormItemProps, renderData: any):SFormItemPropsData => {
  // 如果是函数，就执行
  const { ...rets } = props;
  const renderProps = Object.keys(rets).reduce((prev, key) => {
    const value = props[key];
    if (FUNCTION_CONFIG.includes(key)) {
      prev[key] = value;
      return prev;
    }
    prev[key] = typeof value === 'function' ? value(renderData) : value;
    return prev;
  }, {});
  return renderProps as SFormItemPropsData;
}

export function SFormItem<
    T extends FormItemKeyType = FormItemKeyType,
    U extends SFormComponentClass = SFormComponentClass
>(props: SFormItemProps<T, U>): React.ReactElement {

  const renderData = props.renderData || {};

  const renderProps = useMemo(() => {
    return configFunctionToValue(props, renderData);
  }, [props])


  const {
    formItemKeyMap = null,
    formItem = true,
    label,
    labelTip,
    labelText,
    labelWrapper,
    required,
    formItemKey,
    componentClass,
    className,
    view,
    show = true,
    elementProps = {},
    rules,
    col,
    offsetAfter,
    dependencies,
    watchChildFormValidate,
    hideError = false,
    ...itemProps
  } = renderProps;

  const elementRef = useRef(null);

  const ablerules:any = useMemo(() => {
    const ruleList = [...(rules || [])];
    if (view) return [];
    if (required) {
      if (DefaultRequiredRuleMap[componentClass]) {
        ruleList?.unshift?.({ label, message: `此项必填`, ...DefaultRequiredRuleMap[componentClass] })
      } else {
        ruleList?.unshift?.({ required: true, message: `此项必填` })
      }
    }

    if (watchChildFormValidate) {
      let validateFnName = 'validateFields'
      if (typeof watchChildFormValidate === 'string') {
        validateFnName = watchChildFormValidate
      }
      ruleList.push({
        validator: (rule, value) => {
          if (!elementRef.current?.[validateFnName]) {
            return Promise.resolve()
          }
          // 兜底优化，如果方法返回的不是promise，则promise包裹
          return Promise.resolve(elementRef.current?.[validateFnName]?.())
        }
      });
    }

    return ruleList;
  }, [required, rules, view, watchChildFormValidate])

  const showRequired = useMemo(() => {
    if (view) return false;
    if (required) {
      return true;
    }
    return ablerules.some((rule) => rule.required);
  }, [required, view, ablerules])

  const colProps = useMemo(() => {
    if (typeof col === 'number') {
      return { span: col }
    }
    return col
  }, [col])

  if (!show) return null;

  let item: any = props.render ? (props?.render?.(props?.renderData, renderProps) || null) :
      <SFormElement
          componentClass={componentClass}
          view={view}
          {...elementProps}
      />;

  // 写入item写入ref
  if (watchChildFormValidate && React.isValidElement(item) && !item?.ref?.current) {
    item = React.cloneElement(item as React.ReactElement, { ref: elementRef });
  }

  // 如果是formItem，就包裹一层Form.Item
  const formItemConfig = {
    label: labelTip ? getLabelTip(label, labelTip) : label,
    required: showRequired,
    name: formItemKeyToList(formItemKey),
    rules: ablerules,
    className: cx('s-form-item', {
      'form_item_hidden_error': hideError,
    }, className),
    dependencies: dependencies?.map((n) => formItemKeyToList(n)),
    ...itemProps
  }
  // console.log('formItemConfig依赖', formItemConfig)
  if (labelWrapper) {
    formItemConfig.label = labelWrapper({ label: formItemConfig.label }, renderData)
  }

  if (formItemKeyMap){
    item = <FormItemValueTransform
        keyMap={formItemKeyMap}
        {...formItemConfig}
    >
      {item}
    </FormItemValueTransform>
  } else if (formItem) {
    item = <Form.Item
        {...formItemConfig}
    >
      {item}
    </Form.Item>
  }

  item = <ErrorBoundary
      FallbackComponent={({ error }) => {
        return <Empty
            style={{
              width: '100%',
              minWidth: '400px'
            }}
            imageNode={<Error />}
            text="渲染错误"
            description={'SformItem渲染错误'}
        />
      }}
  >
    {item}
  </ErrorBoundary>

  // 如果有col，就包裹一层Col
  if (!!col) {
    item = <>
      <Col {...colProps}>{item}</Col>
      {offsetAfter && <Col span={offsetAfter} />}
    </>
  }

  return item as React.ReactElement;
}
